"use client";

import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { ColumnDef, Table as TanstackTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type * as React from "react";

import { DataTablePagination } from "./data-table-pagination";

type DataTableProps<TData> = {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  /** Floating bar rendered when rows are selected. Receives selectedRows count. */
  selectionBar?: React.ReactNode;
  isLoading?: boolean;
  totalCount?: number;
  /** Enable checkbox-based row selection. Defaults to false. */
  enableRowSelection?: boolean;
} & React.ComponentProps<"div">;

type ColumnWithSizing = {
  getSize?: () => number;
};

type ColumnDefWithAlign = ColumnDef<unknown> & {
  align?: "left" | "center" | "right" | "inherit" | "justify";
};

export function DataTable<TData>({
  table,
  actionBar,
  selectionBar,
  children,
  className,
  isLoading = false,
  totalCount,
  enableRowSelection = false,
  ...props
}: DataTableProps<TData>) {
  const visibleColumns = table.getVisibleLeafColumns();
  const skeletonColumnCount = Math.max(visibleColumns.length, 1) + (enableRowSelection ? 1 : 0);
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;

  return (
    <Box>
      <Paper
        className={className}
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          position: "relative",
        }}
        variant="outlined"
        {...props}
      >
        {children != null ? (
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
            }}
          >
            {children}
          </Box>
        ) : null}

        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {/* Selection checkbox column header */}
                    {enableRowSelection && (
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Checkbox
                          checked={table.getIsAllPageRowsSelected()}
                          indeterminate={table.getIsSomePageRowsSelected()}
                          onChange={table.getToggleAllPageRowsSelectedHandler()}
                          size="small"
                          inputProps={{ "aria-label": "select all rows" }}
                        />
                      </TableCell>
                    )}
                    {headerGroup.headers.map((header) => {
                      const colSize = (header.column as ColumnWithSizing).getSize?.();
                      const cellStyle =
                        colSize != null
                          ? { width: colSize, minWidth: colSize, maxWidth: colSize }
                          : undefined;
                      const columnDef = header.column.columnDef as ColumnDefWithAlign;
                      const align = columnDef.meta?.align ?? columnDef.align;
                      return (
                        <TableCell
                          colSpan={header.colSpan}
                          key={header.id}
                          style={cellStyle}
                          align={align}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>

              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, rowIndex) => {
                    const skeletonKey = `skeleton-row-${String(rowIndex)}`;
                    return (
                      <TableRow key={skeletonKey} sx={{ height: 52 }}>
                        {Array.from({ length: skeletonColumnCount }).map((__, columnIndex) => (
                          <TableCell key={`${skeletonKey}-cell-${String(columnIndex)}`}>
                            <Skeleton animation="wave" height={20} variant="rounded" width="100%" />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      selected={row.getIsSelected()}
                      sx={enableRowSelection ? { cursor: "pointer" } : undefined}
                      onClick={enableRowSelection ? row.getToggleSelectedHandler() : undefined}
                    >
                      {/* Per-row checkbox */}
                      {enableRowSelection && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                            size="small"
                            inputProps={{ "aria-label": `select row ${row.id}` }}
                          />
                        </TableCell>
                      )}
                      {row.getVisibleCells().map((cell) => {
                        const colSize = (cell.column as ColumnWithSizing).getSize?.();
                        const cellStyle =
                          colSize != null
                            ? { width: colSize, minWidth: colSize, maxWidth: colSize }
                            : undefined;
                        const columnDef = cell.column.columnDef as ColumnDefWithAlign;
                        const align = columnDef.meta?.align ?? columnDef.align;
                        const isActionCell =
                          cell.column.id === "action" || cell.column.id === "actions";
                        return (
                          <TableCell
                            key={cell.id}
                            style={cellStyle}
                            align={align}
                            onClick={isActionCell ? (e) => e.stopPropagation() : undefined}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={table.getAllColumns().length + (enableRowSelection ? 1 : 0)}
                      sx={{ height: 96 }}
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
          }}
        >
          <DataTablePagination table={table} totalCount={totalCount} />
          {actionBar != null && selectedCount > 0 ? actionBar : null}
        </Box>
      </Paper>
      {/* Floating selection bar — slides up when rows are selected */}
      {selectionBar != null && selectedCount > 0 ? selectionBar : null}
    </Box>
  );
}
